import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import WebDatatable from 'app/shared/datatable/web/datatable';
import MobileDatatable from 'app/shared/datatable/mobile/datatable';
import { setPageTitle } from 'store/actions/header';
import { loadEventsToStore } from 'store/actions/event';
import styles from '../event.module.css';
import cx from 'classnames';
import { useEventsService } from 'hooks/automation/event';
import { useNavigate } from 'react-router-dom';
import { useSubscriptionService } from 'hooks/sales/subscription';
import { Button } from 'app/shared/button';
import GmModal from 'app/shared/modal/modal';
import { PixelConfig } from './pixel';
import useScreenSize from 'hooks/size';

export const AutomationEvents = () => {
  const dispatch = useDispatch();
  const navigateTo = useNavigate();
  const { isMobile } = useScreenSize();
  const event_in_store = useSelector((state) => state.events);

  const { fetchEvents, searchEvents } = useEventsService();
  const { fetchSubscription } = useSubscriptionService();

  const [events, setEvents] = useState([]);
  const [has_insights_subscription, setHasInsightsSubscription] = useState(false);
  const [is_search_mode, setSearchMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [total_events, setTotalEvents] = useState(0);
  const [show_pixel_config, setShowPixelConfig] = useState(false);

  useEffect(() => {
    dispatch(setPageTitle([{ title: 'App Analytics', path: '/nsights/app' }]));
    fetchSubscription({ query_string: `plan_code=app_insights,startup&status=active` }).then(
      ({ subscriptions: [subscription] }) => {
        setHasInsightsSubscription(subscription && subscription.id);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setEvents(Object.values(event_in_store));
  }, [event_in_store]);

  const config = {
    actions: {
      single: () => []
    },
    allow_bulk_action: true,
    css: {},
    fields: [
      {
        title: 'Name',
        key: 'name',
        searchable: true,
        isTitle: true
      },
      {
        title: 'Code',
        searchable: true,
        key: 'code',
        isTagline: true
      },
      {
        title: 'Type',
        key: 'type',
        formatter: (value) => {
          switch (value) {
            case 'system':
              return <span className={styles.blueBadge}>System</span>;
            default:
              return <span className={styles.infoBadge}>Custom</span>;
          }
        },
        isBadge: true
      }
    ],
    is_search_mode,
    items: events.sort((a, b) => b.time_stamp - a.time_stamp),
    num_of_rows: 10,
    search_key: 'code,name',
    search_text: ''
  };

  const parseEvents = (data) => {
    const parsed_events = data?.map((event) => {
      const [code] = event.code.split(':');
      const new_element = {
        ...event,
        code
      };
      return new_element;
    });
    return parsed_events;
  };

  const handleDataRequest = async (page = 0, population = 10) => {
    try {
      setLoading(true);
      const { events, size } = await fetchEvents({
        query_string: `page=${page}&type=user&population=${population}&sort_by=-created_on`
      });
      const parsed_events = parseEvents(events);
      dispatch(loadEventsToStore(parsed_events));
      setTotalEvents(size);
    } catch (e) {
      dispatch(loadEventsToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const handleSearchRequest = async (keys, keyword, page = 0, population = 10) => {
    if (!keys) return;
    try {
      setLoading(true);
      const { events, size } = await searchEvents(keys, keyword, {
        query_string: `type=user&sort_by=-created_on&page=${page}&population=${population}`
      });

      const parsed_events = parseEvents(events);
      dispatch(loadEventsToStore(parsed_events));
      setTotalEvents(size);
    } catch (e) {
      dispatch(loadEventsToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const handleItemClick = (data) => {
    if (!has_insights_subscription) return;
    navigateTo(`/insights/app/${data.code}`);
  };

  const table_actions = (
    <>
      <Button text="Create pixel" onClick={() => setShowPixelConfig(true)} />
    </>
  );

  return (
    <div className={cx(styles.wrapper, 'dt-content-wrapper')}>
      {isMobile ? (
        <MobileDatatable
          config={config}
          action={() => {}}
          onClick={handleItemClick}
          onListModeChange={setSearchMode}
          onDataRequest={handleDataRequest}
          onSearchRequest={handleSearchRequest}
          showHeader={false}
        />
      ) : (
        <WebDatatable
          config={{
            ...config,
            total_count: total_events
          }}
          action={() => {}}
          table_actions={table_actions}
          onClick={handleItemClick}
          checkbox
          request_complete={!loading}
          onDataRequest={handleDataRequest}
          loadingSearch={() => {}}
          onSearchRequest={handleSearchRequest}
        />
      )}
      <GmModal
        show_modal={show_pixel_config}
        show_title={true}
        title="Configure website pixel"
        onClose={() => setShowPixelConfig(false)}
      >
        <PixelConfig />
      </GmModal>
    </div>
  );
};
